import React from "react";

/**
 * Renders a country flag as an <img> from flagcdn.com (SVG).
 * Works on all platforms including Windows browsers where emoji flags don't render.
 * Falls back to the emoji flag on error.
 */
export default function FlagImg({
  code,
  size = 20,
  fallback,
  style,
}: {
  code: string;
  size?: number;
  fallback?: string;
  style?: React.CSSProperties;
}) {
  const upperCode = (code || "").toUpperCase();
  const isValid = upperCode.length === 2;
  const src = isValid
    ? `https://flagcdn.com/${upperCode.toLowerCase()}.svg`
    : undefined;

  const emojiFlag = isValid
    ? upperCode
        .split("")
        .map((c) => String.fromCodePoint(127397 + c.charCodeAt(0)))
        .join("")
    : "🏳️";

  if (!src) {
    return (
      <span aria-hidden="true" style={{ fontSize: size, lineHeight: 1, ...style }}>
        {fallback ?? emojiFlag}
      </span>
    );
  }

  return (
    <img
      src={src}
      alt={emojiFlag}
      width={size}
      height={Math.round(size * 0.75)}
      loading="lazy"
      style={{
        display: "inline-block",
        verticalAlign: "middle",
        objectFit: "contain",
        ...style,
      }}
      onError={(e) => {
        // Replace broken img with emoji fallback
        const span = document.createElement("span");
        span.setAttribute("aria-hidden", "true");
        span.style.fontSize = `${size}px`;
        span.style.lineHeight = "1";
        span.textContent = fallback ?? emojiFlag;
        (e.target as HTMLElement).replaceWith(span);
      }}
    />
  );
}
